import { useQuota } from "./useQuota";

/**
 * Custom hook for managing application access control
 * Centralizes all logic for premium vs free user permissions
 * 
 * @returns {Object} Access control flags and quota information
 */
export const useApplicationAccess = () => {
  const { quotaData, isPremium, loading, error } = useQuota();

  // For premium users, all access is granted
  // For free users, check quota remaining
  const hasQuotaRemaining = quotaData?.data?.remaining > 0;

  return {
    // User status
    isPremium,
    isLoading: loading,
    error,

    // Access permissions (premium users bypass all quota checks)
    canAnalyzeNew: isPremium || hasQuotaRemaining,
    canReconfigure: isPremium || hasQuotaRemaining,
    canAccessResumeCoverLetter: true, // Always true for analyzed jobs

    // Quota information (only relevant for free users)
    quota: {
      used: quotaData?.data?.used || 0,
      total: quotaData?.data?.total || 0,
      remaining: quotaData?.data?.remaining || 0,
      resetDate: quotaData?.data?.resetDate,
      tier: quotaData?.data?.tier || "free",
      isUnlimited: quotaData?.data?.isUnlimited || false,
      hasQuotaAvailable: isPremium || hasQuotaRemaining,
    },

    // UI flags
    showQuotaBanner: !isPremium && (quotaData?.data?.remaining <= 2 || quotaData?.data?.remaining === 0),
    showUpgradePrompt: !isPremium && !hasQuotaRemaining,
  };
};

export default useApplicationAccess;
